import { createContext, useContext } from 'react';

export type BriefsEnvironment = 'DEV' | 'PROD' | 'STAGING';
type CartEnv = 'DEV' | 'PROD';
type RequestEnv = 'NON_PRODUCTION' | 'PRODUCTION';

export type BriefConfigType = {
    autoFulfilment: boolean;
    allMultiStepEnabled: boolean;
    cartEnv: CartEnv;
    requestEnv: RequestEnv;
    localCollaborateUrl: string;
    vistaCartUrl?: string;
    statsigServerKey: string;
};

export function getCartEnv(): CartEnv {
    if (process.env['NEXT_PUBLIC_VISTA_CART_ENV']) {
        return process.env['NEXT_PUBLIC_VISTA_CART_ENV'] === 'DEV' ? 'DEV' : 'PROD';
    }
    if (
        typeof window !== 'undefined' &&
        (window.location.hostname.includes('staging') ||
            window.location.hostname.includes('localhost'))
    ) {
        return 'DEV';
    }
    if (process.env['VITE_PUBLIC_VISTA_CART_ENV']) {
        return process.env['VITE_PUBLIC_VISTA_CART_ENV'] === 'DEV' ? 'DEV' : 'PROD';
    }
    return 'PROD';
}

export function getRequestEnv(): RequestEnv {
    if (process.env['NEXT_PUBLIC_REQUEST_ENV']) {
        return process.env['NEXT_PUBLIC_REQUEST_ENV'] === 'NON_PRODUCTION'
            ? 'NON_PRODUCTION'
            : 'PRODUCTION';
    }
    if (
        typeof window !== 'undefined' &&
        (window.location.hostname.includes('staging') ||
            window.location.hostname.includes('localhost'))
    ) {
        return 'NON_PRODUCTION';
    }
    return 'PRODUCTION';
}

// The detault context value uses environment variables to allow those apps to continue without the context provider.
// The ubik version of the brief will use the context provider rather than environment variables.
function getBriefConfig(): BriefConfigType {
    return {
        autoFulfilment:
            process.env.NEXT_PUBLIC_AUTO_FULFILLMENT === 'enabled' ||
            process.env.VITE_AUTO_FULFILLMENT === 'enabled',
        allMultiStepEnabled:
            process.env.NEXT_PUBLIC_BRIEF_CREATE_ROUTE === 'multistep' ||
            process.env.VITE_BRIEF_CREATE_ROUTE === 'multistep',
        cartEnv: getCartEnv(),
        requestEnv: getRequestEnv(),
        localCollaborateUrl: 'http://localhost:3001',
        statsigServerKey: process.env.VITE_STATSIG_SERVER_KEY || '',
        // If we are in dev, then use the dev cart, otherwise, leave this field undefined as per
        // existing.
        vistaCartUrl: process.env.VITE_ESDEV_CART ? 'http://localhost:3009' : undefined,
    };
}

const BriefConfigContext = createContext<BriefConfigType>(getBriefConfig());

export const useBriefConfig = () => {
    const context = useContext(BriefConfigContext);
    if (!context) {
        throw new Error('useBriefConfig must be used within a BriefConfigProvider');
    }
    return context;
};

function getBriefConfigForEnvironment(env: BriefsEnvironment): BriefConfigType {
    switch (env) {
        case 'DEV':
            return {
                autoFulfilment: false, // Override for development
                allMultiStepEnabled: true,
                cartEnv: 'DEV',
                requestEnv: 'NON_PRODUCTION',
                localCollaborateUrl: 'http://localhost:3000',
                // TODO: Remove https://vistaprint.atlassian.net/browse/ESMTCM-501?atlOrigin=eyJpIjoiYjVhN2E2YzlmNmMwNDFjMGEyZGI5MDA5YzU1N2RiMTQiLCJwIjoiaiJ9
                statsigServerKey: 'secret-f8ajDqbiIrt2VTjA7b8EywUWPVApMkyEkEajqeNqGKb',
                vistaCartUrl: 'http://localhost:3009', // Local development cart URL
            };
        case 'STAGING':
            return {
                autoFulfilment: false,
                allMultiStepEnabled: true,
                cartEnv: 'DEV',
                requestEnv: 'NON_PRODUCTION',
                localCollaborateUrl: 'http://localhost:3000',
                statsigServerKey: 'secret-f8ajDqbiIrt2VTjA7b8EywUWPVApMkyEkEajqeNqGKb',
            };
        default:
            return {
                autoFulfilment: false,
                allMultiStepEnabled: true,
                cartEnv: 'PROD',
                requestEnv: 'PRODUCTION',
                localCollaborateUrl: 'http://localhost:3000',
                statsigServerKey: 'secret-f8ajDqbiIrt2VTjA7b8EywUWPVApMkyEkEajqeNqGKb',
            };
    }
}

export const BriefConfigProvider = ({
    children,
    env,
}: {
    children: React.ReactNode;
    env: BriefsEnvironment;
}) => {
    return (
        <BriefConfigContext.Provider value={getBriefConfigForEnvironment(env)}>
            {children}
        </BriefConfigContext.Provider>
    );
};
